import { useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import {
  ColorType,
  CrosshairMode,
  LineStyle,
  createChart,
} from 'lightweight-charts';
import type { IChartApi, IPriceLine, ISeriesApi, UTCTimestamp } from 'lightweight-charts';

import { terminal } from '../app/terminal.js';
import { useApp, useQuotes } from '@weke/ui';
import { aggregateBars, applyLivePrice } from '@weke/ui';
import type { Candle } from '@weke/ui';
import { formatPrice, formatSignedPrice, formatVolume, priceDelta, priceDigits } from '@weke/ui';
import { INTERVALS, bucketOf, chartTime } from '@weke/ui';

import { sortedPositions, workingOrders } from '@weke/ui';

/**
 * The chart: M1 bars from `history`, folded into M5/M15/H1/H4/D1, with the
 * trader's own positions and working orders drawn on it.
 *
 * ## The one float in the app, and why it is contained
 *
 * Lightweight Charts plots `number`s. Prices are scaled integers everywhere else,
 * so at the boundary they are converted with `plotPrice` **for pixel coordinates
 * only**: the crosshair readout, the axis labels, the ticket and every P&L value
 * use the original canonical strings, and nothing that comes back out of the
 * library is ever treated as a price. Scaling by 1e8 and printing with the
 * symbol's digits reproduces the exact server value; the chart never computes a
 * margin, a P&L or a level.
 *
 * ## Live movement
 *
 * History is polled when the symbol or interval changes; between polls the last
 * candle's close follows the live quote (`applyLivePrice`), which widens the
 * high/low with the same integer comparisons. A quote in a *new* bucket triggers
 * a refetch instead of inventing an open for a bar `history` has not stored yet.
 */
export function ChartPanel(props: {
  readonly symbolName: string;
  readonly minutes: number;
  readonly onInterval: (minutes: number) => void;
  readonly footer?: ReactNode;
}): ReactElement {
  const app = useApp();
  const { quotes } = useQuotes();
  const symbol = app.symbols.find((entry) => entry.name === props.symbolName);
  const interval = INTERVALS.find((entry) => entry.minutes === props.minutes)?.id ?? 'M1';
  const [candles, setCandles] = useState<readonly Candle[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');
  const [hover, setHover] = useState<Candle | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const linesRef = useRef<IPriceLine[]>([]);
  const candleMapRef = useRef<Map<number, Candle>>(new Map());
  const bucketRef = useRef<string | null>(null);

  const minutes = props.minutes;
  const digits = symbol === undefined ? 5 : priceDigits(symbol.digits, symbol.tick_size_e8);

  /* Create the chart once. */
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: '#10141b' },
        textColor: '#94a0b3',
        fontFamily: "'JetBrains Mono', 'Cascadia Mono', Consolas, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: '#1b212c' },
        horzLines: { color: '#1b212c' },
      },
      rightPriceScale: { borderColor: '#232c39', entireTextOnly: true },
      timeScale: { borderColor: '#232c39', timeVisible: true, secondsVisible: false, barSpacing: 8, rightOffset: 4 },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#5b93ff', width: 1, style: LineStyle.Dotted, labelBackgroundColor: '#1b212c' },
        horzLine: { color: '#5b93ff', width: 1, style: LineStyle.Dotted, labelBackgroundColor: '#1b212c' },
      },
      localization: {
        priceFormatter: (price: number): string => formatPrice(plotToScaled(price), digits),
      },
    });
    const series = chart.addCandlestickSeries({
      upColor: '#3ddc84',
      downColor: '#ff5d5d',
      wickUpColor: '#3ddc84',
      wickDownColor: '#ff5d5d',
      borderVisible: false,
      priceFormat: { type: 'price', precision: digits, minMove: 1 / 10 ** digits },
    });
    /**
     * Tick volume along the bottom, the way a desk reads activity. It is the
     * **count of ticks** that formed each bar (server-supplied `tick_count`,
     * summed exactly in `lib/bars.ts`), not traded volume — this is a quote
     * feed, and inventing a traded volume would be a lie with a number on it.
     */
    const volume = chart.addHistogramSeries({
      priceScaleId: 'volume',
      priceFormat: { type: 'volume' },
      priceLineVisible: false,
      lastValueVisible: false,
    });
    volume.priceScale().applyOptions({ scaleMargins: { top: 0.86, bottom: 0 } });
    chartRef.current = chart;
    seriesRef.current = series;
    volumeRef.current = volume;

    chart.subscribeCrosshairMove((param) => {
      const time = param.time;
      if (typeof time !== 'number') {
        setHover(null);
        return;
      }
      setHover(candleMapRef.current.get(time) ?? null);
    });

    const observer = new ResizeObserver(() => {
      chart.applyOptions({ width: container.clientWidth, height: container.clientHeight });
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      volumeRef.current = null;
      linesRef.current = [];
    };
  }, [digits]);

  /* Load history when the symbol or interval changes, and keep it fresh. */
  useEffect(() => {
    if (symbol === undefined) return;
    let cancelled = false;
    setStatus('loading');
    const load = async (): Promise<void> => {
      const bars = await terminal.bars(symbol.name, minutes);
      if (cancelled) return;
      const folded = aggregateBars(bars, (iso) => bucketOf(iso, minutes));
      setCandles(folded);
      // A fresh stack has minutes of history: say so instead of drawing an
      // empty grid as if the market were closed.
      setStatus(folded.length === 0 ? 'empty' : 'ready');
    };
    void load().catch(() => {
      if (!cancelled) setStatus('error');
    });
    // History arrives late on a new stack (ticks → bars), so a chart that opened
    // before the first minute closed fills in by itself.
    const timer = setInterval(() => void load().catch(() => undefined), 15_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [symbol, minutes]);

  /* Push candles into the series and remember them for the crosshair. */
  useEffect(() => {
    const series = seriesRef.current;
    if (series === null) return;
    const data = candles.map((candle) => ({
      time: chartTime(candle.bucket) as UTCTimestamp,
      open: plotPrice(candle.open_e8),
      high: plotPrice(candle.high_e8),
      low: plotPrice(candle.low_e8),
      close: plotPrice(candle.close_e8),
    }));
    const map = new Map<number, Candle>();
    for (const candle of candles) map.set(chartTime(candle.bucket), candle);
    candleMapRef.current = map;
    series.setData(data);
    volumeRef.current?.setData(
      candles.map((candle) => ({
        time: chartTime(candle.bucket) as UTCTimestamp,
        value: Number(candle.ticks),
        color:
          BigInt(candle.close_e8) >= BigInt(candle.open_e8)
            ? 'rgba(61, 220, 132, 0.28)'
            : 'rgba(255, 93, 93, 0.28)',
      })),
    );
    if (data.length > 0) series.priceScale().applyOptions({ autoScale: true });
    // fitContent on a two-bar chart draws one enormous candle; only fit once
    // there is enough history for the axis to mean something.
    if (data.length >= 10) chartRef.current?.timeScale().fitContent();
    else chartRef.current?.timeScale().scrollToRealTime();
    const last = candles[candles.length - 1];
    bucketRef.current = last === undefined ? null : last.bucket;
  }, [candles]);

  /* Follow the live quote in the newest bucket. */
  useEffect(() => {
    if (symbol === undefined) return;
    const quote = quotes[symbol.name];
    const series = seriesRef.current;
    if (quote === undefined || series === null || candles.length === 0) return;
    const bucket = bucketOf(new Date(quote.receivedAtMs).toISOString(), minutes);
    const price = quote.bid;
    const next = applyLivePrice(candles, bucket, price);
    if (next === null) {
      // A new bucket the history service has not stored yet: refetch rather than
      // fabricate an open price for it.
      void terminal.bars(symbol.name, minutes).then((bars) => {
        const folded = aggregateBars(bars, (iso) => bucketOf(iso, minutes));
        if (folded.length > 0) setCandles(folded);
      });
      return;
    }
    const last = next[next.length - 1];
    if (last === undefined) return;
    if (last.bucket !== bucketRef.current) {
      bucketRef.current = last.bucket;
      setCandles(next);
      return;
    }
    // In-place update: the library is fast at this and it keeps the crosshair
    // and the scroll position where the trader left them.
    series.update({
      time: chartTime(last.bucket) as UTCTimestamp,
      open: plotPrice(last.open_e8),
      high: plotPrice(last.high_e8),
      low: plotPrice(last.low_e8),
      close: plotPrice(last.close_e8),
    });
    volumeRef.current?.update({
      time: chartTime(last.bucket) as UTCTimestamp,
      value: Number(last.ticks),
      color:
        BigInt(last.close_e8) >= BigInt(last.open_e8)
          ? 'rgba(61, 220, 132, 0.28)'
          : 'rgba(255, 93, 93, 0.28)',
    });
    candleMapRef.current.set(chartTime(last.bucket), last);
  }, [candles, minutes, quotes, symbol]);

  /* Levels and entry markers for this symbol. */
  useEffect(() => {
    const series = seriesRef.current;
    if (series === null || symbol === undefined) return;
    for (const line of linesRef.current) series.removePriceLine(line);
    linesRef.current = [];
    const add = (priceE8: string, color: string, title: string, style: LineStyle): void => {
      linesRef.current.push(
        series.createPriceLine({
          price: plotPrice(priceE8),
          color,
          lineWidth: 1,
          lineStyle: style,
          axisLabelVisible: true,
          title,
        }),
      );
    };
    const positions = sortedPositions(app.book).filter((row) => row.symbolId === symbol.id);
    const orders = workingOrders(app.book).filter((row) => row.symbolId === symbol.id);
    for (const position of positions) {
      add(position.openPrice, '#5b93ff', `${position.side} ${position.positionId.slice(0, 6)}`, LineStyle.Solid);
      if (position.sl !== null) add(position.sl, '#ff5d5d', 'SL', LineStyle.Dashed);
      if (position.tp !== null) add(position.tp, '#3ddc84', 'TP', LineStyle.Dashed);
    }
    for (const order of orders) {
      if (order.limitPrice !== null) add(order.limitPrice, '#e8b23a', `${order.side} limit`, LineStyle.Dotted);
      if (order.stopPrice !== null) add(order.stopPrice, '#e8b23a', `${order.side} stop`, LineStyle.Dotted);
    }
    const first = candles[0];
    if (first !== undefined) {
      series.setMarkers(
        positions
          .map((position) => {
            const time = chartTime(position.openedAt);
            return {
              time: Math.max(time, chartTime(first.bucket)) as UTCTimestamp,
              position: position.side === 'BUY' ? ('belowBar' as const) : ('aboveBar' as const),
              color: position.side === 'BUY' ? '#3ddc84' : '#ff5d5d',
              shape: position.side === 'BUY' ? ('arrowUp' as const) : ('arrowDown' as const),
              text: `${position.side} ${formatVolume(position.volume, symbol.volume_step_e4)}`,
            };
          })
          .filter((marker) => Number.isFinite(marker.time)),
      );
    }
  }, [app.book, candles, symbol]);

  const readout = hover ?? candles[candles.length - 1] ?? null;
  const reference = symbol === undefined ? undefined : app.dayOpen[symbol.name];
  const change =
    readout === null || reference === undefined ? null : priceDelta(readout.close_e8, reference);

  return (
    <section className="panel min-h-0 flex-1" aria-label="Chart">
      <header className="panel-head">
        <span className="text-text">{symbol?.name ?? "—"}</span>
        <span>{interval}</span>
        {readout === null ? null : (
          <span className="num normal-case tracking-normal">
            O {formatPrice(readout.open_e8, digits)} H {formatPrice(readout.high_e8, digits)} L{" "}
            {formatPrice(readout.low_e8, digits)} C {formatPrice(readout.close_e8, digits)}
            {change === null ? null : (
              <span className={change.startsWith("-") ? "loss" : /^0+$/.test(change) ? "muted" : "gain"}>
                {" "}
                {formatSignedPrice(change, digits)}
              </span>
            )}
          </span>
        )}
        <span role="group" aria-label="Chart interval" className="ml-auto flex gap-0.5">
          {INTERVALS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className="chip"
              aria-pressed={entry.minutes === minutes}
              onClick={() => props.onInterval(entry.minutes)}
            >
              {entry.label}
            </button>
          ))}
        </span>
      </header>
      <div className="panel-body relative bg-chart">
        <div ref={containerRef} className="absolute inset-0" data-testid="chart" />
        {/* The canvas cannot be asserted on; this is the candle count. */}
        <span className="hidden" data-testid="chart-candles">
          {candles.length}
        </span>
        {status !== "ready" ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <p className="muted text-xs">
              {status === "loading"
                ? "Loading candles…"
                : status === "empty"
                  ? `No history for ${symbol?.name ?? "this symbol"} yet — the demo feed stores bars as ticks arrive.`
                  : "History is unavailable right now."}
            </p>
          </div>
        ) : null}
        <div className="pointer-events-none absolute top-1 left-2 text-2xs text-faint">
          {symbol?.name ?? "—"} · {interval} · bid candles
          {quotes[symbol?.name ?? ""] === undefined
            ? " · awaiting quotes"
            : ` · ${quotes[symbol?.name ?? ""]?.source ?? ""}`}
        </div>
      </div>
      {props.footer}
    </section>
  );
}

/**
 * Scaled price → plot coordinate. **Plotting only.**
 *
 * `Number(BigInt) / 1e8` is exact for every price this platform trades (well
 * inside the 53-bit mantissa for values below ~900,000), and the value is never
 * read back as a money amount: the readout above formats the original string.
 */
function plotPrice(scaled: string): number {
  return Number(BigInt(scaled)) / 1e8;
}

/** A plot coordinate back to a scaled string, for the axis label only. */
function plotToScaled(price: number): string {
  return BigInt(Math.round(price * 1e8)).toString();
}
